import { Alert, Badge, Box, Button, Grid, Group, Paper, Select, Slider, Stack, Table, Text, Title, Tooltip } from "@mantine/core";
import { IconAlertTriangle, IconChartHistogram, IconDrone } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Map as OlMap, View } from "ol";
import { defaults as defaultControls } from "ol/control";
import GeoJSON from "ol/format/GeoJSON";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import { Fill, Stroke, Style } from "ol/style";
import type ImageTile from "ol/ImageTile";
import { useEffect, useMemo, useRef, useState } from "react";

import { authFetch } from "../../api/client";
import { http } from "../../api/http";
import { fmtNum } from "../../components/ui";
import { useT } from "../../i18n/script";

interface IndexDef { code: string; name: string; formula: string; value_min: number; value_max: number; colormap: string; target: string }
interface Survey { id: number; site_id: number; survey_date: string; sensor: string | null; gsd_cm: number | null; status: string;
  assets: { id: number; kind: string; status: string }[] }
interface Zonal {
  index: string; formula: string; mean: number; min: number; max: number; std: number; median: number; pixel_count: number;
  histogram: { counts: number[]; edges: number[] }; percentiles: Record<string, number>;
}

const RAMPS: Record<string, string[]> = {
  rdylgn: ["#a50026", "#f46d43", "#fee08b", "#a6d96a", "#006837"],
  viridis: ["#440154", "#21918c", "#fde725"],
  blues: ["#f7fbff", "#6baed6", "#08306b"],
  brbg: ["#8c510a", "#f5f5f5", "#01665e"],
  brbg_r: ["#01665e", "#f5f5f5", "#8c510a"],
  oranges: ["#fff5eb", "#7f2704"],
};
const READY = new Set(["ready", "valid"]);

/**
 * UAV indeksi (VI-03, VI-05): karta izabranog indeksa nad multispektralnim ortofotom voćnjaka (dinamičke pločice)
 * i zonalna statistika za granicu voćnjaka (srednja, medijana, opseg, percentili, histogram). Rezultat se može
 * sačuvati u istoriju voćnjaka i tada se prikazuje zajedno sa Sentinel-2 serijom.
 */
export default function UavIndexPanel({ orchardId, siteId, onSaved }: { orchardId: number; siteId: number | null | undefined; onSaved?: () => void }) {
  const { t } = useT();
  const defs = useQuery({ queryKey: ["index-defs"], staleTime: 600_000, queryFn: () => http.get<IndexDef[]>("/api/v1/indices/definitions") });
  // snimanja lokaliteta voćnjaka ili ona čiji obuhvat preklapa granicu voćnjaka
  const surveys = useQuery({ queryKey: ["uav-surveys", "orchard", orchardId, siteId],
    queryFn: () => http.get<Survey[]>("/api/v1/uav/surveys", { orchard_id: orchardId }) });

  const options = useMemo(() => (surveys.data ?? []).flatMap((s) => s.assets
    .filter((a) => a.kind === "ortho_ms" && READY.has(a.status))
    .map((a) => ({ value: String(a.id), label: `${s.survey_date} · ${s.sensor ?? "UAV"}${s.gsd_cm ? ` · ${s.gsd_cm} cm` : ""}` }))), [surveys.data]);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>("NDVI");
  const [opacity, setOpacity] = useState(85);
  useEffect(() => { if (!assetId && options.length) setAssetId(options[0].value); }, [options, assetId]);

  const def = defs.data?.find((d) => d.code === code);
  const zonal = useMutation({
    mutationFn: () => http.post<Zonal>("/api/v1/indices/zonal-stats",
      { asset_id: Number(assetId), index_code: code, target_type: "orchard", target_id: orchardId, save: true }),
    onSuccess: () => onSaved?.(),
  });
  useEffect(() => { zonal.reset(); }, [assetId, code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (surveys.isSuccess && !options.length) return null; // voćnjak nije sniman dronom — panel se ne prikazuje
  return (
    <Paper withBorder radius="lg" p="md">
      <Group justify="space-between" mb="sm" wrap="wrap">
        <div>
          <Group gap={8}><IconDrone size={20} color="var(--mantine-color-agri-8, #1f4d36)" />
            <Title order={4}>{t("UAV indeksi (multispektralni ortofoto)")}</Title></Group>
          <Text size="sm" c="dimmed">{t("Karta indeksa nad snimkom dronom i zonalna statistika za granicu voćnjaka.")}</Text>
        </div>
      </Group>
      {(
        <Grid gap="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="sm">
              <Select label={t("Snimanje")} data={options} value={assetId} onChange={setAssetId} allowDeselect={false} />
              <Select label={t("Indeks")} searchable allowDeselect={false} value={code} onChange={setCode}
                data={(defs.data ?? []).filter((d) => d.target !== "soil" || d.code.endsWith("_MS"))
                  .map((d) => ({ value: d.code, label: `${d.code} — ${d.name}` }))}
                renderOption={({ option }) => <Text size="sm" translate="no">{option.label}</Text>}
                description={def ? <span data-no-translit>{def.formula}</span> : undefined} />
              <div>
                <Text size="sm" fw={500}>{t("Prozirnost sloja")}</Text>
                <Slider value={opacity} onChange={setOpacity} min={20} max={100} step={5} label={(v) => `${v}%`} />
              </div>
              {def && <Legend def={def} />}
              <Button leftSection={<IconChartHistogram size={16} />} loading={zonal.isPending} disabled={!assetId || !code}
                onClick={() => zonal.mutate()}>{t("Zonalna statistika voćnjaka")}</Button>
              {zonal.isError && <Alert color="red" variant="light" icon={<IconAlertTriangle />}>{(zonal.error as Error).message}</Alert>}
              {zonal.data && <ZonalResult z={zonal.data} />}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Box pos="relative" h={460} style={{ borderRadius: 12, overflow: "hidden" }}>
              <UavIndexMap orchardId={orchardId} assetId={assetId ? Number(assetId) : null} code={code} opacity={opacity / 100} />
            </Box>
          </Grid.Col>
        </Grid>
      )}
    </Paper>
  );
}

function Legend({ def }: { def: IndexDef }) {
  const ramp = RAMPS[def.colormap] ?? RAMPS.rdylgn;
  return (
    <div>
      <Box h={12} style={{ borderRadius: 6, background: `linear-gradient(90deg, ${ramp.join(", ")})` }} />
      <Group justify="space-between"><Text size="xs">{def.value_min}</Text><Text size="xs">{def.value_max}</Text></Group>
    </div>
  );
}

function ZonalResult({ z }: { z: Zonal }) {
  const { t } = useT();
  const peak = Math.max(1, ...z.histogram.counts);
  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" mb={6}><Text fw={700} size="sm">{z.index}</Text>
        <Tooltip label={t("Rezultat je sačuvan u istoriju voćnjaka")}><Badge size="sm" color="green" variant="light">{t("sačuvano")}</Badge></Tooltip></Group>
      <Table fz="sm" verticalSpacing={2}>
        <Table.Tbody>
          {([["Srednja vrijednost", z.mean], ["Medijana", z.median], ["Minimum", z.min], ["Maksimum", z.max], ["Std. devijacija", z.std],
            ["p5 – p95", null]] as [string, number | null][]).map(([l, v]) => (
            <Table.Tr key={l}><Table.Td>{t(l)}</Table.Td>
              <Table.Td ta="right">{v === null ? `${fmtNum(z.percentiles["5"], 3)} – ${fmtNum(z.percentiles["95"], 3)}` : fmtNum(v, 3)}</Table.Td></Table.Tr>
          ))}
          <Table.Tr><Table.Td>{t("Broj piksela")}</Table.Td><Table.Td ta="right">{fmtNum(z.pixel_count, 0)}</Table.Td></Table.Tr>
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed" mt={6}>{t("Histogram")}</Text>
      <Group gap={1} align="flex-end" h={60} wrap="nowrap">
        {z.histogram.counts.map((c, i) => (
          <Tooltip key={i} label={`${z.histogram.edges[i]} … ${z.histogram.edges[i + 1]}: ${c}`}>
            <Box style={{ flex: 1, height: `${(100 * c) / peak}%`, minHeight: c ? 2 : 0, background: "var(--mantine-color-agri-6)", borderRadius: 2 }} />
          </Tooltip>
        ))}
      </Group>
      <Group justify="space-between"><Text size="xs">{z.histogram.edges[0]}</Text><Text size="xs">{z.histogram.edges[z.histogram.edges.length - 1]}</Text></Group>
    </Paper>
  );
}

function UavIndexMap({ orchardId, assetId, code, opacity }: { orchardId: number; assetId: number | null; code: string | null; opacity: number }) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const idxLayer = useRef(new TileLayer({ zIndex: 5 }));
  const boundary = useRef(new VectorSource());

  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const map = new OlMap({
      target: el.current,
      controls: defaultControls({ attributionOptions: { collapsible: true } }),
      view: new View({ center: [1913000, 5584000], zoom: 16, maxZoom: 23 }),
      layers: [
        new TileLayer({ source: new XYZ({ url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          maxZoom: 19, crossOrigin: "anonymous", attributions: "Esri, Maxar, Earthstar Geographics" }) }),
        idxLayer.current,
        new VectorLayer({ source: boundary.current, zIndex: 10,
          style: new Style({ stroke: new Stroke({ color: "#ffffff", width: 2.5 }), fill: new Fill({ color: "rgba(0,0,0,0)" }) }) }),
      ],
    });
    mapRef.current = map;
    return () => { map.setTarget(undefined); mapRef.current = null; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    http.get<{ type: string; features: unknown[] }>("/api/v1/orchards/geojson", { ids: [orchardId] }).then((fc) => {
      if (cancelled) return;
      boundary.current.clear();
      boundary.current.addFeatures(new GeoJSON().readFeatures(fc, { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" }) as never);
      const ext = boundary.current.getExtent();
      if (ext && Number.isFinite(ext[0])) mapRef.current?.getView().fit(ext, { padding: [30, 30, 30, 30], maxZoom: 20 });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [orchardId]);

  useEffect(() => {
    if (!assetId || !code) { idxLayer.current.setSource(null); return; }
    // pločice traže prijavu: učitavaju se preko authFetch (token), a ne direktno iz <img>
    const src = new XYZ({ url: `/api/v1/indices/tiles/${encodeURIComponent(code)}/{z}/{x}/{y}.png?asset_id=${assetId}`, maxZoom: 23 });
    src.setTileLoadFunction((tile, url) => {
      const img = (tile as ImageTile).getImage() as HTMLImageElement;
      authFetch(url).then(async (r) => {
        if (r.status !== 200) { img.src = "data:image/gif;base64,R0lGODlhAQABAAAAACw="; return; }
        const u = URL.createObjectURL(await r.blob());
        img.onload = () => URL.revokeObjectURL(u);
        img.src = u;
      }).catch(() => { img.src = "data:image/gif;base64,R0lGODlhAQABAAAAACw="; });
    });
    idxLayer.current.setSource(src);
  }, [assetId, code]);

  useEffect(() => { idxLayer.current.setOpacity(opacity); }, [opacity]);

  return <div ref={el} style={{ position: "absolute", inset: 0 }} />;
}
